import React from 'react';
import { DjayIllustrationProps } from '../types';
import { DjayIllustrationBase } from '../DjayIllustrationBase';
import { DjayAvatar } from '../../DjayAvatar';
/**
 * RainbowIllustration — Achievement art for the 'rainbow-djay' achievement.
 *
 * Unlocks the legendary Rainbow Djay variant when the learner has collected
 * all six learning-progress variants (Pixel, Blaze, Frost, Royal, Cosmic,
 * Shadow). Visual language: a soft rainbow arc on a pastel sky, with floating
 * color dots, ending in celebrating Djay holding the tripleStars accessory.
 */
export function RainbowIllustration({
  achievementId,
  variant = 'rainbow',
  isUnlocked,
  rarity,
  size = 'md',
  animate = true,
  className = ''
}: DjayIllustrationProps) {
  return <DjayIllustrationBase isUnlocked={isUnlocked} rarity={rarity} size={size} className={className} data-id="element-3584">
      <div className="relative w-full h-full bg-gradient-to-b from-sky-100 via-pink-50 to-amber-50 overflow-hidden" data-id="element-3585">
        {/* Background: Soft Rainbow Arc */}
        <svg className="absolute inset-0 w-full h-full z-0" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" data-id="element-3586">
          {/* Rainbow arc — concentric strokes (outer → inner): red, orange, yellow, green, blue, violet */}
          <g className="origin-center" style={{
          transformOrigin: '100px 220px'
        }} data-id="element-3587">
            <circle cx="100" cy="220" r="160" stroke="#F87171" strokeWidth="10" fill="none" opacity={isUnlocked ? 0.7 : 0.25} data-id="element-3588" />
            <circle cx="100" cy="220" r="150" stroke="#FB923C" strokeWidth="10" fill="none" opacity={isUnlocked ? 0.7 : 0.25} data-id="element-3589" />
            <circle cx="100" cy="220" r="140" stroke="#FBBF24" strokeWidth="10" fill="none" opacity={isUnlocked ? 0.7 : 0.25} data-id="element-3590" />
            <circle cx="100" cy="220" r="130" stroke="#34D399" strokeWidth="10" fill="none" opacity={isUnlocked ? 0.7 : 0.25} data-id="element-3591" />
            <circle cx="100" cy="220" r="120" stroke="#60A5FA" strokeWidth="10" fill="none" opacity={isUnlocked ? 0.7 : 0.25} data-id="element-3592" />
            <circle cx="100" cy="220" r="110" stroke="#A78BFA" strokeWidth="10" fill="none" opacity={isUnlocked ? 0.7 : 0.25} data-id="element-3593" />
          </g>

          {/* Soft clouds at the rainbow bases */}
          <ellipse cx="30" cy="160" rx="24" ry="10" fill="white" opacity="0.85" data-id="element-3594" />
          <ellipse cx="20" cy="170" rx="18" ry="8" fill="white" opacity="0.7" data-id="element-3595" />
          <ellipse cx="170" cy="160" rx="24" ry="10" fill="white" opacity="0.85" data-id="element-3596" />
          <ellipse cx="180" cy="170" rx="18" ry="8" fill="white" opacity="0.7" data-id="element-3597" />
        </svg>

        {/* Djay */}
        <div className="absolute inset-0 z-10 flex items-center justify-center pt-[5%]" data-id="element-3598">
          <div className="w-[70%] h-[70%] flex items-center justify-center" data-id="element-3599">
            <DjayAvatar size="hero" emotion={isUnlocked ? 'celebrating' : 'thinking'} variant={variant} accessory="tripleStars" animate={animate && isUnlocked} className="w-full h-full" data-id="element-3600" />
          </div>
        </div>

        {/* Foreground: Floating color dots (only when unlocked) */}
        {isUnlocked && <svg className="absolute inset-0 w-full h-full z-20 pointer-events-none" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-3601">
            <circle cx="30" cy="40" r="3" fill="#F87171" opacity="0.85" data-id="element-3602" />
            <circle cx="170" cy="35" r="3" fill="#FB923C" opacity="0.85" data-id="element-3603" />
            <circle cx="50" cy="70" r="2" fill="#FBBF24" opacity="0.8" data-id="element-3604" />
            <circle cx="150" cy="65" r="2" fill="#34D399" opacity="0.8" data-id="element-3605" />
            <circle cx="25" cy="100" r="2.5" fill="#60A5FA" opacity="0.85" data-id="element-3606" />
            <circle cx="175" cy="105" r="2.5" fill="#A78BFA" opacity="0.85" data-id="element-3607" />

            {/* Sparkle stars */}
            <path d="M40 20 L42 25 L47 26 L43 30 L44 35 L40 32 L36 35 L37 30 L33 26 L38 25 Z" fill="#FDE047" opacity="0.9" data-id="element-3608" />
            <path d="M160 25 L162 30 L167 31 L163 35 L164 40 L160 37 L156 40 L157 35 L153 31 L158 30 Z" fill="#F472B6" opacity="0.9" data-id="element-3609" />
          </svg>}
      </div>
    </DjayIllustrationBase>;
}